// src/utils/timeCalc.js

// 0.5単位で切り捨てる関数
export function floorToHalfHour(hours) {
  // 浮動小数点誤差対策を含めて 0.5 単位で floor する
  // 例: 0.9 -> 0.5, 0.4 -> 0.0, 1.25 -> 1.0, 1.6 -> 1.5
  const adjusted = Math.floor((hours + 0.001) * 2) / 2;
  return Math.max(0, adjusted);
}

// 時間文字列をDateオブジェクトに変換する関数
function parseTimeStr(str) {
  if (!str) return null;
  // 全角などを半角に
  const cleanStr = String(str).trim().replace(/[０-９：]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
  
  const [h, m] = cleanStr.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;

  const dayOffset = Math.floor(h / 24);
  const hour = h % 24;
  
  // 計算基準日（2024/01/01）
  return new Date(2024, 0, 1 + dayOffset, hour, m);
}

// ★メインの計算関数
export function calculateWorkTime(startStr, endStr, instructionStr, breakStr) {
  const zeroResult = { duration: 0, overtime: 0, nightOvertime: 0, nightBonus: 0 };

  if (!startStr || !endStr) return zeroResult;

  const start = parseTimeStr(startStr);
  const end = parseTimeStr(endStr);
  const instruction = instructionStr ? parseTimeStr(instructionStr) : start;

  if (!start || !end || !instruction) return zeroResult;

  // ---------------------------------------------------------
  // 1. 指示時間の適用（重要）
  // ---------------------------------------------------------
  // 指示時間(instruction)より早く来た(start)場合は、指示時間を開始(adjustedStart)とみなす
  // 指示時間が未入力の場合は start と同じになるため start が採用される
  const adjustedStart = start < instruction ? instruction : start;
  
  if (end <= adjustedStart) return zeroResult;

  // ---------------------------------------------------------
  // 2. 休憩時間の数値化
  // ---------------------------------------------------------
  let breakHours = 0;
  if (breakStr) {
    const cleanStr = String(breakStr).replace('：', ':');
    if (cleanStr.includes(':')) {
      const [bh, bm] = cleanStr.split(':').map(Number);
      if (!isNaN(bh)) {
        breakHours = bh + ((parseFloat(bm) || 0) / 60);
      }
    } else {
      const val = parseFloat(cleanStr);
      if (!isNaN(val)) breakHours = val;
    }
  }

  // ---------------------------------------------------------
  // 3. 時間計算（拘束時間ベース）
  // ---------------------------------------------------------
  
  // 拘束時間（時間単位）: ここですでに「指示時間」が考慮されています
  const elapsedHours = (end - adjustedStart) / 3600000;

  // 実働時間（拘束 - 休憩）
  const realWorkHours = Math.max(0, elapsedHours - breakHours);
  
  // ★重要修正: 残業時間は「拘束時間(elapsedHours)」から8時間を引く（ご指定のルール）
  const overtimeRaw = Math.max(0, elapsedHours - 8);

  // ---------------------------------------------------------
  // 4. 深夜時間の計算
  // ---------------------------------------------------------
  const earlyMorningStart = new Date(2024, 0, 1, 0, 0);
  const earlyMorningEnd   = new Date(2024, 0, 1, 6, 0); 
  const nightStart        = new Date(2024, 0, 1, 22, 0);        
  const nightEnd          = new Date(2024, 0, 2, 6, 0);         

  const getOverlap = (s, e, rangeS, rangeE) => {
    const startMax = s > rangeS ? s : rangeS;
    const endMin = e < rangeE ? e : rangeE;
    return Math.max(0, (endMin - startMax) / 3600000);
  };

  // 深夜帯にいた総時間
  const nightRaw = getOverlap(adjustedStart, end, earlyMorningStart, earlyMorningEnd) 
                 + getOverlap(adjustedStart, end, nightStart, nightEnd);
  
  // 深夜残業の計算
  // 「拘束時間が8時間を超えたタイミング」以降の深夜時間を計算します
  let nightOvertimeRaw = 0;
  if (elapsedHours > 8) {
    // 開始から8時間後の時刻（休憩含む）
    const overtimeStartTime = new Date(adjustedStart.getTime() + 8 * 60 * 60 * 1000);
    
    nightOvertimeRaw = getOverlap(overtimeStartTime, end, earlyMorningStart, earlyMorningEnd) 
                     + getOverlap(overtimeStartTime, end, nightStart, nightEnd);
  }

  // ---------------------------------------------------------
  // 5. 結果の返却（0.5単位で切り捨て）
  // ---------------------------------------------------------
  return {
    duration: floorToHalfHour(realWorkHours),      // 実働（休憩を引いたもの）
    overtime: floorToHalfHour(overtimeRaw),        // 普通残業（拘束8h超え分）
    nightOvertime: floorToHalfHour(nightOvertimeRaw), // 深夜残業
    nightBonus: floorToHalfHour(Math.max(0, nightRaw - nightOvertimeRaw)) // 深夜割増
  };
}